// Onglet Frais & débours : toutes les avances du cabinet, tous dossiers confondus (selon les droits).
// On voit ce qui reste à refacturer, depuis quand, et les justificatifs manquants ;
// on coche des lignes d'un même dossier puis « Préparer la facture ».
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { FilterPill } from '../ui/FilterPill';
import { ListToolbar, Segments, ToolbarGrow, ToolbarSearch } from '../ui/ListToolbar';
import { FaCircleCheck, FaClock, FaReceipt } from '../ui/FaIcons';
import { cn } from '../../lib/cn';
import { formatAppDate } from '../../lib/dateFormat';
import { formatEuros } from '../../services/billing/billingService';
import type { DossierExpense } from '../../services/billing/expenseService';
import { useExpenses } from '../../hooks/queries/useExpenses';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { EXPENSE_NATURE_LABELS, expenseAge, type ExpenseNature } from '../../lib/billing/expenses';
import {
  expensesByDossier,
  expensesKpis,
  filterExpenses,
  OLD_EXPENSE_DAYS,
  type ExpenseSegment,
} from '../../lib/billing/expensesTab';
import { ExpenseFormDialog } from './ExpenseFormDialog';
import { ExpenseReceiptLink, NATURE_PILL } from './DossierExpensesSection';
import { PrepareInvoiceDialog } from './PrepareInvoiceDialog';
import { planPreparation, type PreparationPlan } from '../../lib/billing/preparation';
import { toast } from 'sonner';

const NATURE_OPTIONS = [
  { value: 'debours', label: 'Débours' },
  { value: 'frais', label: 'Frais' },
];
const RECEIPT_OPTIONS = [
  { value: 'missing', label: 'Justificatif manquant' },
  { value: 'attached', label: 'Justificatif joint' },
];

function Kpi({ icon, label, value, hint, tone }: { icon: ReactNode; label: string; value: string; hint: string; tone?: 'warn' | 'late' | 'ok' }) {
  return (
    <div
      className={cn(
        'grid grid-cols-[32px_minmax(0,1fr)] items-start gap-x-3 rounded-xl border border-neutral-200 bg-white px-4 py-3.5',
        tone === 'warn' && 'border-t-2 border-t-cime-gold',
      )}
    >
      <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-[#f4f1e8] text-cime-text-muted [&_svg]:h-3.5 [&_svg]:w-3.5">{icon}</span>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">{label}</p>
        <b
          className={cn(
            'mt-[3px] block text-[22px] font-semibold tabular-nums',
            tone === 'late' ? 'text-cime-urgent' : tone === 'ok' ? 'text-[#2f7a55]' : 'text-cime-text-primary',
          )}
        >
          {value}
        </b>
        <small className="mt-0.5 block text-xs text-neutral-400">{hint}</small>
      </div>
    </div>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n > 1 ? many : one}`;

export function BillingExpensesTab({ onCount }: { onCount?: (count: number) => void }) {
  const canCreate = useCanAccessFeature('expenses_create');
  const { data: expenses, isLoading } = useExpenses();
  const { data: dossiers } = useDossiers({ include_archived: true, include_closed: true });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<DossierExpense | null>(null);
  const [segment, setSegment] = useState<ExpenseSegment>('todo');
  const [search, setSearch] = useState('');
  const [natures, setNatures] = useState<string[]>([]);
  const [receipts, setReceipts] = useState<string[]>([]);
  const [dossierFilter, setDossierFilter] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [plan, setPlan] = useState<Extract<PreparationPlan<DossierExpense>, { ok: true }> | null>(null);

  function prepare(selection: DossierExpense[]) {
    const next = planPreparation(selection);
    if (!next.ok) {
      toast.error(next.reason);
      return;
    }
    setPlan(next);
  }

  const dossierMap = useMemo(() => {
    const m = new Map<string, { number: string; title: string }>();
    for (const d of dossiers ?? []) m.set(d.id, { number: d.number, title: d.title });
    return m;
  }, [dossiers]);

  const now = useMemo(() => new Date(), []);
  const all = expenses ?? [];
  const rows = useMemo(
    () =>
      all.map((e) => {
        const dossier = dossierMap.get(e.dossier_id);
        return {
          ...e,
          haystack: [e.label, e.supplier, e.piece_number, dossier?.number, dossier?.title].filter(Boolean).join(' ').toLowerCase(),
        };
      }),
    [all, dossierMap],
  );
  const visible = useMemo(
    () => filterExpenses(rows, { segment, q: search, natures, receipts, dossiers: dossierFilter }),
    [rows, segment, search, natures, receipts, dossierFilter],
  );
  const kpis = useMemo(() => expensesKpis(all, now), [all, now]);
  const byDossier = useMemo(() => expensesByDossier(all), [all]);
  const todoCount = useMemo(() => all.filter((e) => !e.billed_at).length, [all]);
  const dossierOptions = useMemo(() => {
    const ids = [...new Set(all.map((e) => e.dossier_id))];
    return ids.map((id) => ({ value: id, label: dossierMap.get(id)?.number ?? 'Dossier' }));
  }, [all, dossierMap]);

  // Une ligne cochée qui sort de la liste (filtre, refacturation) est décochée.
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(visible.filter((r) => !r.billed_at).map((r) => r.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visible]);

  useEffect(() => {
    if (!isLoading) onCount?.(todoCount);
  }, [todoCount, isLoading, onCount]);

  const selection = all.filter((e) => selected.has(e.id));
  const selectionTotal = selection.reduce((s, e) => s + Math.round(Number(e.amount_ttc) * 100), 0) / 100;
  const selectionDossiers = new Set(selection.map((e) => e.dossier_id)).size;
  const filtersChanged = natures.length > 0 || receipts.length > 0 || dossierFilter.length > 0;
  const showStatus = segment !== 'todo';

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-[10px] border border-[#e6e2d8] bg-[#fbfaf6] px-3.5 py-3 text-[13px] leading-[1.45] text-cime-text-muted">
          <b className="text-cime-text-primary">Débours</b> : somme avancée au nom du client (greffe, commissaire de justice, expert). Refacturée à l’euro près, <b className="text-cime-text-primary">sans TVA</b>, sur une facture de débours.
        </div>
        <div className="rounded-[10px] border border-[#e6e2d8] bg-[#fbfaf6] px-3.5 py-3 text-[13px] leading-[1.45] text-cime-text-muted">
          <b className="text-cime-text-primary">Frais</b> : dépense du cabinet liée au dossier (déplacement, copies, affranchissement). Refacturée <b className="text-cime-text-primary">avec TVA</b>.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Kpi
          icon={<FaReceipt />}
          tone="warn"
          label="Débours à refacturer"
          value={formatEuros(kpis.deboursAmount)}
          hint={`${plural(kpis.deboursLines, 'ligne')} · ${plural(kpis.deboursDossiers, 'dossier')}`}
        />
        <Kpi icon={<FaReceipt />} tone="warn" label="Frais à refacturer" value={formatEuros(kpis.fraisAmount)} hint={`HT · ${plural(kpis.fraisLines, 'ligne')}`} />
        <Kpi
          icon={<FaClock />}
          tone={kpis.oldAmount > 0 ? 'late' : undefined}
          label={`Avancé depuis plus de ${OLD_EXPENSE_DAYS} jours`}
          value={formatEuros(kpis.oldAmount)}
          hint={kpis.oldLines > 0 ? `${plural(kpis.oldLines, 'ligne')} à refacturer vite` : 'Aucune avance ancienne'}
        />
        <Kpi
          icon={<FaCircleCheck />}
          tone="ok"
          label="Refacturé ce mois"
          value={formatEuros(kpis.billedMonthAmount)}
          hint={`sur ${plural(kpis.billedMonthPieces, 'facture')}`}
        />
      </div>

      <ListToolbar>
        <Segments
          label="Lignes affichées"
          value={segment}
          onChange={(v) => setSegment(v as ExpenseSegment)}
          options={[
            { value: 'todo', label: 'À refacturer', count: todoCount },
            { value: 'billed', label: 'Refacturés' },
            { value: 'all', label: 'Tous' },
          ]}
        />
        <ToolbarSearch value={search} onChange={setSearch} placeholder="Rechercher un libellé, un dossier…" ariaLabel="Rechercher un frais ou un débours" className="w-[260px]" />
        <FilterPill label="Nature" options={NATURE_OPTIONS} selected={natures} onChange={setNatures} />
        <FilterPill label="Justificatif" options={RECEIPT_OPTIONS} selected={receipts} onChange={setReceipts} />
        <FilterPill label="Dossier" options={dossierOptions} selected={dossierFilter} onChange={setDossierFilter} />
        {filtersChanged && (
          <button
            type="button"
            onClick={() => { setNatures([]); setReceipts([]); setDossierFilter([]); }}
            className="text-xs text-[#8a928c] underline underline-offset-2 hover:text-[#1f2823]"
          >
            Réinitialiser
          </button>
        )}
        <ToolbarGrow />
        {canCreate ? (
          <Button variant="primary" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="w-4 h-4 mr-1.5" /> Frais ou débours
          </Button>
        ) : null}
      </ListToolbar>

      <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_272px]">
        <div className="min-w-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : visible.length === 0 ? (
            <Card>
              <div className="py-12 text-center text-neutral-500">
                <FaReceipt className="mx-auto mb-3 h-9 w-9 text-neutral-300" aria-hidden />
                <p className="text-sm">{all.length > 0 ? 'Aucune ligne ne correspond à ces filtres.' : 'Aucun frais ni débours pour l’instant.'}</p>
              </div>
            </Card>
          ) : (
            <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              <div className="overflow-x-auto">
                <table className="cime-table text-[13px]">
                  <thead>
                    <tr>
                      <th className="w-10 py-2 pl-[18px]" aria-label="Sélection" />
                      <th className="py-2 px-2.5 text-left">Dépense</th>
                      <th className="py-2 px-2.5 text-left">Dossier</th>
                      <th className="py-2 px-2.5 text-left">Nature</th>
                      <th className="py-2 px-2.5 text-right">Montant</th>
                      <th className="py-2 px-2.5 text-left">Justificatif</th>
                      {showStatus ? <th className="py-2 px-2.5 text-left">Statut</th> : null}
                      <th className="py-2 px-2.5 pr-[18px] text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((e) => {
                      const dossier = dossierMap.get(e.dossier_id);
                      const age = expenseAge(e.piece_date, now);
                      const locked = !!e.billed_at;
                      const checked = selected.has(e.id);
                      return (
                        <tr
                          key={e.id}
                          onClick={() => { if (!locked && canCreate) { setEditing(e); setFormOpen(true); } }}
                          className={cn(!locked && canCreate && 'cursor-pointer', checked && 'bg-cime-primary/5')}
                        >
                          <td className="py-[9px] pl-[18px]" onClick={(ev) => ev.stopPropagation()}>
                            {locked ? null : (
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(e.id)}
                                aria-label={`Sélectionner ${e.label}`}
                                className="h-4 w-4 cursor-pointer accent-[#043429]"
                              />
                            )}
                          </td>
                          <td className="max-w-[280px] px-2.5 py-[9px] leading-tight">
                            <span className="block truncate text-[13px] font-medium text-cime-text-primary">{e.label}</span>
                            {e.supplier || e.piece_number ? (
                              <span className="block truncate text-xs text-neutral-400">{[e.supplier, e.piece_number].filter(Boolean).join(' · ')}</span>
                            ) : null}
                          </td>
                          <td className="max-w-[200px] px-2.5 py-[9px] leading-tight">
                            {dossier ? (
                              <span title={dossier.title}>
                                <span className="block text-[11px] tracking-wide text-neutral-400">{dossier.number}</span>
                                <span className="block truncate text-[13px] text-cime-text-primary">{dossier.title}</span>
                              </span>
                            ) : (
                              <span className="text-neutral-400">—</span>
                            )}
                          </td>
                          <td className="px-2.5 py-[9px]">
                            <span className={cn('inline-flex h-[22px] items-center rounded-full px-2.5 text-[11px] font-semibold', NATURE_PILL[e.nature as ExpenseNature])}>
                              {EXPENSE_NATURE_LABELS[e.nature as ExpenseNature]}
                            </span>
                          </td>
                          <td className="px-2.5 py-[9px] text-right leading-tight tabular-nums">
                            <span className="block font-semibold text-cime-text-primary">{formatEuros(e.nature === 'frais' ? e.amount_ht : e.amount_ttc)}</span>
                            <span className="block text-[11px] text-neutral-400">{e.nature === 'frais' ? `HT · TVA ${String(Number(e.vat_rate)).replace('.', ',')} %` : 'sans TVA'}</span>
                          </td>
                          <td className="px-2.5 py-[9px]" onClick={(ev) => ev.stopPropagation()}>
                            <ExpenseReceiptLink expense={e} />
                          </td>
                          {showStatus ? (
                            <td className="px-2.5 py-[9px]">
                              <span className={cn('text-xs font-semibold', locked ? 'text-[#2f7a55]' : 'text-cime-gold-text')}>
                                {locked ? `Refacturé le ${formatAppDate(e.billed_at!, { year: false })}` : 'À refacturer'}
                              </span>
                            </td>
                          ) : null}
                          <td className="whitespace-nowrap px-2.5 py-[9px] pr-[18px] leading-tight">
                            <span className="block text-cime-text-primary">{formatAppDate(e.piece_date)}</span>
                            {!locked ? (
                              <span className={cn('block text-[11px]', age > OLD_EXPENSE_DAYS ? 'font-semibold text-cime-urgent' : 'text-neutral-400')}>
                                {age === 0 ? 'aujourd’hui' : `il y a ${age} j`}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {selection.length > 0 ? (
                <div className="m-3 flex items-center gap-2 rounded-full bg-cime-primary py-2 pl-4 pr-2 text-[13px] text-white">
                  <b className="font-semibold">{plural(selection.length, 'ligne cochée', 'lignes cochées')}</b>
                  <span>· {formatEuros(selectionTotal)}{selectionDossiers > 1 ? ` · ${selectionDossiers} dossiers` : ''}</span>
                  <span className="flex-1" />
                  <button type="button" onClick={() => setSelected(new Set())} className="h-7 rounded-full bg-white/15 px-3 font-medium hover:bg-white/25">
                    Décocher
                  </button>
                  <button
                    type="button"
                    onClick={() => prepare(selection)}
                    className="h-7 rounded-full bg-white px-3 font-semibold text-cime-primary disabled:opacity-60"
                  >
                    Préparer la facture
                  </button>
                </div>
              ) : null}
              <div className="flex justify-between border-t border-[#e6e2d8] px-[18px] py-2.5 text-xs text-neutral-400">
                <span>{plural(visible.length, 'ligne')}</span>
              </div>
            </div>
          )}
        </div>

        <aside aria-label="À refacturer par dossier" className="rounded-xl border border-neutral-200 bg-white">
          <header className="flex items-baseline justify-between border-b border-[#e6e2d8] px-4 py-2.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">À refacturer par dossier</h3>
            <span className="text-xs text-neutral-400">{byDossier.length}</span>
          </header>
          {byDossier.length === 0 ? (
            <p className="px-4 py-4 text-xs text-neutral-400">Rien à refacturer.</p>
          ) : (
            <ul>
              {byDossier.map((row) => {
                const dossier = dossierMap.get(row.dossier_id);
                const active = dossierFilter.length === 1 && dossierFilter[0] === row.dossier_id;
                return (
                  <li key={row.dossier_id}>
                    <button
                      type="button"
                      onClick={() => setDossierFilter(active ? [] : [row.dossier_id])}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 border-b border-neutral-100 px-4 py-2.5 text-left last:border-b-0 hover:bg-neutral-50',
                        active && 'bg-cime-primary/5',
                      )}
                    >
                      <span className="min-w-0 leading-tight">
                        <span className="block text-[11px] tracking-wide text-neutral-400">{dossier?.number ?? 'Dossier'}</span>
                        <span className="block truncate text-[13px] text-cime-text-primary">{dossier?.title ?? '—'}</span>
                      </span>
                      <span className="shrink-0 text-right leading-tight">
                        <b className="block font-semibold tabular-nums text-cime-text-primary">{formatEuros(row.amount)}</b>
                        <small className="text-[11px] text-neutral-400">{plural(row.lines, 'ligne')}</small>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>

      <ExpenseFormDialog open={formOpen} onClose={() => setFormOpen(false)} expense={editing} />
      <PrepareInvoiceDialog
        plan={plan}
        onClose={(done) => {
          setPlan(null);
          if (done) setSelected(new Set());
        }}
      />
    </div>
  );
}
